// Pure lookup for the "same physical device, other protocol" relationship.
//
// A boat running both a native NMEA 2000 gateway and a Signal K server lists one
// physical unit TWICE — once per protocol — and the two rows are deliberately
// never merged (core `wireDeviceId` keeps protocols apart so cloud rows can't
// fork or merge on the read surface). What the rows do share is
// `busIdentityFingerprint` (`n2kphys:<mfrCode>:<uniqueNumber>`), which both
// protocol views derive from the same bus identity.
//
// The user-visible problem this solves: only one of the twins carries the rich
// self-reported detail (a SIM100's channel labels), so the other renders the
// generic catalog name and an empty page, and looks like a broken second device.
// This module answers the one question the UI needs — "is there a richer twin of
// this device I should point at?" — and nothing else. It never merges, reorders,
// or hides rows; the caller renders a one-line pointer.

import { deviceChannelRows } from './deviceChannels';
import type { VesselDevice } from './useBoatDevices';

/** A usable fingerprint: present, a string, and not empty. */
function fingerprintOf(device: VesselDevice): string | null {
  const raw: unknown = device.busIdentityFingerprint;
  if (typeof raw !== 'string' || raw === '') return null;
  return raw;
}

/**
 * The same physical device seen through another protocol, when that other row
 * carries channel information this one lacks. `null` otherwise — which is the
 * answer for nearly every device on a boat.
 *
 * A candidate qualifies only when ALL of these hold:
 * - both rows have a non-empty `busIdentityFingerprint` and the two are equal
 *   (no fingerprint means no established bus identity, so no claim of sameness);
 * - it is a different `deviceId`, so the device can never point at itself;
 * - it is genuinely richer — it reports channels and `device` reports none.
 *   A device that already shows its own channels needs no pointer.
 *
 * Tie-break when several qualify (a bus with three protocol views of one unit,
 * or a duplicated feed): most channel rows first, then the lexicographically
 * smallest `deviceId`. Deterministic regardless of the order `all` arrives in,
 * so the pointer does not flip between refetches.
 */
export function findRicherTwin(
  device: VesselDevice,
  all: readonly VesselDevice[]
): VesselDevice | null {
  const fingerprint = fingerprintOf(device);
  if (fingerprint === null) return null;
  // Already showing its own channels: it is the rich side, or both sides are
  // equally bare. Either way there is nothing more to point at.
  if (deviceChannelRows(device).length > 0) return null;

  let best: VesselDevice | null = null;
  let bestChannels = 0;
  for (const candidate of all) {
    if (candidate.deviceId === device.deviceId) continue;
    if (fingerprintOf(candidate) !== fingerprint) continue;
    const channels = deviceChannelRows(candidate).length;
    if (channels === 0) continue;
    if (
      best === null ||
      channels > bestChannels ||
      (channels === bestChannels && candidate.deviceId < best.deviceId)
    ) {
      best = candidate;
      bestChannels = channels;
    }
  }
  return best;
}
